import React, { useState, useEffect } from 'react';
import { UserProfile } from '../types';
import { generateOfflineChallenge, generateBookRecommendation } from '../services/geminiService';
import { Smartphone, Book, Sunrise, Moon, Coffee, Users, Clock, Loader2, RefreshCw, CheckCircle2 } from 'lucide-react';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const DigitalBalanceHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [loadingChallenge, setLoadingChallenge] = useState(false);
  const [challenge, setChallenge] = useState<{title: string, desc: string, difficulty: string} | null>(null);
  
  const [loadingBook, setLoadingBook] = useState(false);
  const [book, setBook] = useState<{title: string, author: string, reason: string} | null>(null);

  const usagePercentage = Math.min((user.appUsage.dailyMinutes / 240) * 100, 100); // 4 hours max
  const isOverLimit = user.appUsage.dailyMinutes > 240;

  const handleGetChallenge = async () => {
      setLoadingChallenge(true);
      try {
          const res = await generateOfflineChallenge(user);
          setChallenge(res);
      } catch (e) {
          console.error(e);
      } finally {
          setLoadingChallenge(false);
      }
  };

  const handleGetBook = async () => {
      setLoadingBook(true);
      try {
          const res = await generateBookRecommendation(user);
          setBook(res);
      } catch (e) {
          console.error(e);
      } finally {
          setLoadingBook(false);
      }
  };

  const handleLogSocial = () => {
      onUpdateUser({
          ...user,
          lifeProfile: {
              ...user.lifeProfile,
              social: {
                  ...user.lifeProfile.social,
                  lastSocialInteraction: Date.now()
              }
          }
      });
  };

  const daysSinceSocial = Math.floor((Date.now() - (user.lifeProfile.social.lastSocialInteraction || 0)) / (1000 * 60 * 60 * 24));

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="mb-6">
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <Sunrise className="w-6 h-6 text-orange-500" />
             تعادل دیجیتال
           </h2>
           <p className="text-slate-500 text-sm mt-1">بازگشت به دنیای واقعی و لذت بردن از لحظات آفلاین</p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-8">
          
          {/* Usage Status */}
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm relative overflow-hidden">
               <div className={`absolute top-0 left-0 w-2 h-full ${isOverLimit ? 'bg-red-500' : 'bg-emerald-500'}`}></div>
               <div className="flex justify-between items-start mb-4 pl-4">
                   <div>
                       <h3 className="font-bold text-slate-800 text-lg">زمان استفاده امروز</h3>
                       <p className={`text-sm mt-1 ${isOverLimit ? 'text-red-500 font-bold' : 'text-slate-500'}`}>
                           {isOverLimit ? '⚠️ استفاده بیش از حد مجاز (۴ ساعت)' : '✅ وضعیت متعادل'}
                       </p>
                   </div>
                   <div className="text-right">
                       <span className="text-3xl font-black text-slate-700">{Math.floor(user.appUsage.dailyMinutes / 60)}</span>
                       <span className="text-sm text-slate-400 mr-1">ساعت</span>
                       <span className="text-3xl font-black text-slate-700 ml-2">{user.appUsage.dailyMinutes % 60}</span>
                       <span className="text-sm text-slate-400 mr-1">دقیقه</span>
                   </div>
               </div>
               
               <div className="w-full bg-slate-100 h-4 rounded-full overflow-hidden pl-4">
                   <div 
                        className={`h-full rounded-full transition-all duration-1000 ${isOverLimit ? 'bg-red-500' : 'bg-emerald-500'}`}
                        style={{ width: `${usagePercentage}%` }}
                   ></div>
               </div>
               
               {isOverLimit && (
                   <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-xl text-sm flex items-center gap-2 pl-4">
                       <Moon className="w-4 h-4" />
                       پیشنهاد می‌کنم کمی استراحت کنی و به چشم‌هات استراحت بدی.
                   </div>
               )}
          </div>

          {/* Social Health Check */}
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                  <div className={`p-4 rounded-full ${daysSinceSocial > 7 ? 'bg-red-100 text-red-500' : 'bg-blue-100 text-blue-500'}`}>
                      <Users className="w-8 h-8" />
                  </div>
                  <div>
                      <h3 className="font-bold text-slate-800">وضعیت اجتماعی</h3>
                      <p className="text-sm text-slate-500 mt-1">
                          {daysSinceSocial > 30 ? 'مدت زیادیه تنهایی!' : daysSinceSocial > 7 ? 'هفته کم‌ارتباطی داشتی.' : 'ارتباطاتت خوبه!'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">آخرین دیدار: {user.lifeProfile.social.lastSocialInteraction ? `${daysSinceSocial} روز پیش` : 'نامشخص'}</p>
                  </div>
              </div>
              <button 
                onClick={handleLogSocial}
                className="px-6 py-3 bg-blue-50 text-blue-600 rounded-xl font-bold hover:bg-blue-100 transition-colors whitespace-nowrap">
                  امروز دوستم رو دیدم!
              </button>
          </div>

          {/* Offline Challenge */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-6 text-white shadow-lg flex flex-col">
                  <div className="flex items-center gap-2 mb-4 opacity-90">
                      <Coffee className="w-5 h-5" />
                      <span className="font-bold">چالش آفلاین امروز</span>
                  </div>
                  
                  {challenge ? (
                      <div className="flex-1 animate-fade-in">
                          <h4 className="text-xl font-bold mb-2">{challenge.title}</h4>
                          <p className="opacity-90 leading-relaxed mb-4">{challenge.desc}</p>
                          <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs backdrop-blur-sm">
                              سطح: {challenge.difficulty}
                          </span>
                      </div>
                  ) : (
                      <div className="flex-1 flex items-center justify-center text-center opacity-70">
                          <p>یک چالش بگیر و موبایل رو بذار کنار!</p>
                      </div>
                  )}

                  <button 
                    onClick={handleGetChallenge}
                    disabled={loadingChallenge}
                    className="mt-6 w-full py-3 bg-white text-indigo-600 rounded-xl font-bold hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
                  >
                      {loadingChallenge ? <Loader2 className="animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      {challenge ? 'چالش بعدی' : 'دریافت چالش'}
                  </button>
              </div>

              {/* Book Recommendation */}
              <div className="bg-amber-50 rounded-3xl p-6 border border-amber-100 text-amber-900 flex flex-col">
                   <div className="flex items-center gap-2 mb-4 text-amber-600">
                      <Book className="w-5 h-5" />
                      <span className="font-bold">کتاب‌خوانی فیزیکی</span>
                  </div>

                  {book ? (
                      <div className="flex-1 animate-fade-in">
                          <h4 className="text-xl font-bold mb-1">"{book.title}"</h4>
                          <p className="text-sm font-medium opacity-70 mb-3">اثر {book.author}</p>
                          <p className="text-sm leading-relaxed opacity-90 bg-white/50 p-3 rounded-xl border border-amber-200/50">
                              {book.reason}
                          </p>
                      </div>
                  ) : (
                      <div className="flex-1 flex items-center justify-center text-center opacity-60">
                          <p>دنبال یه کتاب کاغذی خوب می‌گردی؟</p>
                      </div>
                  )}

                  <button 
                    onClick={handleGetBook}
                    disabled={loadingBook}
                    className="mt-6 w-full py-3 bg-white border border-amber-200 text-amber-700 rounded-xl font-bold hover:bg-amber-100 transition-colors flex items-center justify-center gap-2"
                  >
                      {loadingBook ? <Loader2 className="animate-spin" /> : <Book className="w-4 h-4" />}
                      پیشنهاد کتاب
                  </button>
              </div>
          </div>
      </div>
    </div>
  );
};

export default DigitalBalanceHub;
